<script setup>
import { ref, reactive, onMounted, computed } from 'vue'

import SharedElementPagination from "@/components/shared/SharedElementPagination.vue";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import HeaderTitle from '@/components/layout/HeaderTitle.vue'
import ProviderService from '@/api/services/provider.service';
import EmployeeService from '@/api/services/employee.service';
import Notification from '@/utils/plugins/NotificationService';
import { useUiStore } from "@/app/stores/uiStore";
import { filterByNormalizedText } from "@/utils/textSearch";

const providers = ref([])
const collaborators = ref([])
const collaboratorSearchQuery = ref('')
const providerCollaborators = ref([])

const tempProviderType = [{
  id: 1,
  name: 'Servicio',
  value: 'service'
}, {
  id: 2,
  name: 'Material',
  value: 'material'
}, {
  id: 3,
  name: 'Suministro',
  value: 'supply'
}, {
  id: 4,
  name: 'Otros',
  value: 'other'
}]

const page = ref(1)
const itemsPerPage = ref(10)
const totalResults = ref(0)
const searchFilterProviderName = ref('')
const searchFilterProviderType = ref()

const createOrUpdateProvider = ref(false)
const deleteDialogShown = ref(false)
const selectedProvider = ref(null)

const providerName = ref('')
const providerPhone = ref('')
const providerEmail = ref('')
const providerDescription = ref('')
const providerPointPerson = ref('')
const providerType = ref()
const providerWeb = ref('')

const uiStore = useUiStore();

const errorProvider = reactive({
  name: false,
  type: false
})

const formatProviderType = (type) => {
  return tempProviderType.find((item) => item.value === type)?.name || '--'
}

const filteredCollaborators = computed(() =>
  filterByNormalizedText(
    collaborators.value,
    collaboratorSearchQuery.value,
    (item) => item.fullName || `${item.name} ${item.lastName}`
  )
)

const filterCollaboratorsByName = (query) => {
  collaboratorSearchQuery.value = query
}

const getProviders = async (query = '') => {
  const params = {
    page: page.value,
    itemsPerPage: itemsPerPage.value
  }

  if (query.length > 0) {
    params.name = query
  }

  if (searchFilterProviderType.value) {
    params.type = searchFilterProviderType.value
  }

  ProviderService.getProviders(params).then((res) => {
    providers.value = res.data['hydra:member']
    totalResults.value = res.data['hydra:totalItems']
  })
}

const deleteProvider = async () => {
  try {
    await ProviderService.deleteProvider(selectedProvider.value.id)
    Notification.addNotification({ message: 'Proveedor eliminado', type: 'success' })
    deleteDialogShown.value = false
    selectedProvider.value = null
    await getProviders(searchFilterProviderName.value)
  } catch {
    Notification.addNotification('Error al eliminar proveedor', 'error', 6000)
  }
}

const resetForm = () => {
  providerName.value = ''
  providerPhone.value = ''
  providerEmail.value = ''
  providerDescription.value = ''
  providerPointPerson.value = ''
  providerType.value = null
  providerWeb.value = ''
  providerCollaborators.value = []
  errorProvider.name = false
  errorProvider.type = false
}

const createNewProvider = () => {
  selectedProvider.value = null
  resetForm()
  createOrUpdateProvider.value = true
}

const closeCreateOrUpdateDialog = () => {
  createOrUpdateProvider.value = false
  resetForm()
}

const editProvider = (row) => {
  selectedProvider.value = row
  providerName.value = row.legalName
  providerPhone.value = row.phone || ''
  providerEmail.value = row.email || ''
  providerType.value = row.type || null
  providerDescription.value = row.serviceDescription || ''
  providerPointPerson.value = row.contactPerson || ''
  providerWeb.value = row.web || ''
  providerCollaborators.value = row.employees || []
  createOrUpdateProvider.value = true
}

const openDeleteDialog = (provider) => {
  selectedProvider.value = provider
  deleteDialogShown.value = true
}

const getParams = () => {
  return {
    legalName: providerName.value,
    phone: providerPhone.value,
    email: providerEmail.value,
    serviceDescription: providerDescription.value,
    contactPerson: providerPointPerson.value,
    type: providerType.value,
    active: true,
    web: providerWeb.value,
    employees: providerCollaborators.value,
  }
}

const validate = () => {
  errorProvider.name = !providerName.value
  errorProvider.type = !providerType.value
  return errorProvider.name || errorProvider.type
}

const handleUpdateProvider = async () => {
  if (validate()) return
  const params = getParams()

  try {
    if (selectedProvider.value) {
      const res = await ProviderService.updateProvider(selectedProvider.value.id, params);
      if (res.status === 200) {
        Notification.addNotification({ message: 'Proveedor actualizado', type: 'success' })
      } else {
        Notification.addNotification({ message: 'Ha habido un problema al actualizar el proveedor', type: 'error' })
      }
    } else {
      const res = await ProviderService.postProvider(params);
      if (res.status === 201) {
        Notification.addNotification({ message: 'Proveedor creado', type: 'success' })
      } else {
        Notification.addNotification({ message: 'Ha habido un problema en la creación del proveedor', type: 'error' })
      }
    }
    createOrUpdateProvider.value = false
    await getProviders(searchFilterProviderName.value)
  } catch {
    Notification.addNotification('Error en operación', 'error', 6000)
  }
}

const search = () => {
  page.value = 1
  getProviders(searchFilterProviderName.value)
}

onMounted(() => {
  getCollaborators()
  getProviders()
})

const getCollaborators = () => {
  const params = {
    page: 1,
    itemsPerPage: 9999,
    employeeType: 'EXTERNAL_COLLABORATOR'
  }
  uiStore.startLoading()
  EmployeeService.getEmployees(params)
    .then((res) => {
      collaborators.value = res.data['hydra:member']
      uiStore.stopLoading()
    })
    .catch(() => {
      Notification.addNotification('Error al intentar conseguir los colaboradores.', 'error', 6000)
      uiStore.stopLoading()
    })
}

function ensureHttp() {
  if (!providerWeb.value) return

  const cleaned = providerWeb.value
    .replace(/^https?:\/\//i, '')
    .trim()

  providerWeb.value = `http://${cleaned}`
}
</script>

<template>
  <div>
    <HeaderTitle title="Producción" />
    <div class="d-flex flex-row justify-content-start">
      <img src="@/assets/icons/group.svg" width="30" alt="">
      <div class="title-text pl-4">
        Proveedores
      </div>
    </div>

    <el-card class="card-search w-100 wback" body-style="padding:0">
      <div class="provider-search">
        <div class="row">
          <div class="col-md-4 col-12">
            <div class="form-item date-picker label-common">
              <label class="w-100 mb-2">Nombre</label>
              <el-input v-model="searchFilterProviderName" placeholder="Nombre del proveedor" clearable
                @change="search()" />
            </div>
          </div>
          <div class="col-md-4 col-12">
            <div class="form-item date-picker label-common">
              <label class="w-100 mb-2">Tipo</label>
              <el-select v-model="searchFilterProviderType" filterable clearable class="w-100"
                placeholder="Tipo del proveedor" collapse-tags collapse-tags-tooltip :max-collapse-tags="2"
                @change="search()">
                <el-option v-for="item in tempProviderType" :key="item.id" :label="item.name" :value="item.value" />
              </el-select>
            </div>
          </div>
          <div class="col-md-4 col-12 d-flex align-items-end">
            <div class="provider-search-actions w-100">
              <button @click="createNewProvider"
                class="btn btn_darkpink btn-search provider-search-button provider-search-button--primary">
                Nuevo proveedor
              </button>
              <button class="btn btn_yellow btn-search provider-search-button" @click="search()">
                Buscar
              </button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <div class="table table-not-mobile">
      <div v-if="providers && providers.length > 0">
        <table>
          <thead>
            <tr>
              <th>Nombre del proveedor</th>
              <th>Tipo</th>
              <th>Persona responsable</th>
              <th>Correo</th>
              <th>Web</th>
              <th>Telefono</th>
              <th class="provider-actions-col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="provider in providers" :key="provider.id">
              <td>{{ provider.legalName }}</td>
              <td>{{ formatProviderType(provider.type) }}</td>
              <td>{{ provider.contactPerson || '--' }}</td>
              <td>{{ provider.email || '--' }}</td>
              <td>
                <a v-if="provider.web" :href="provider.web" target="_blank" rel="noopener noreferrer">
                  {{ provider.web }}
                </a>
                <span v-else>--</span>
              </td>
              <td>{{ provider.phone || '--' }}</td>
              <td class="provider-actions-col">
                <div class="buttons d-inline-flex">
                  <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Editar"
                    @click="editProvider(provider)">
                    <font-awesome-icon icon="circle" style="color: #f7d20e" />
                    <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                  <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Dar de baja"
                    @click="openDeleteDialog(provider)">
                    <font-awesome-icon icon="circle" style="color:red" />
                    <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                  </font-awesome-layers>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <h5>No se han encontrado proveedores</h5>
      </div>
      <SharedElementPagination class="pagination"
        v-model:current-page="page" :total="totalResults" v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;"
        @change="page = $event; search()" show-always />
    </div>

    <div class="table-mobile">
      <div v-if="providers && providers.length > 0">
        <el-row v-for="provider in providers" :key="provider.id" class="mt-3">
          <el-card class="provider-mobile-card w-100 pointer" @click="editProvider(provider)">
            <div class="provider-mobile-card__delete">
              <font-awesome-layers class="fa-2x pointer" title="Dar de baja" @click.stop="openDeleteDialog(provider)">
                <font-awesome-icon icon="circle" style="color:red" />
                <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
              </font-awesome-layers>
            </div>
            <div class="provider-mobile-card__header">
              <div class="provider-mobile-card__title">
                {{ provider.legalName }}
              </div>
            </div>
            <div class="provider-mobile-card__item">
              <span><b class="provider-mobile-card__label">Tipo:</b>
                {{ formatProviderType(provider.type) }}</span>
            </div>
            <div class="provider-mobile-card__item">
              <span><b class="provider-mobile-card__label">Persona responsable:</b>
                {{ provider.contactPerson || '--' }}</span>
            </div>
            <div class="provider-mobile-card__item">
              <span><b class="provider-mobile-card__label">Correo:</b>
                {{ provider.email || '--' }}</span>
            </div>
            <div class="provider-mobile-card__item">
              <span><b class="provider-mobile-card__label">Web: </b>
                <a v-if="provider.web" :href="provider.web" target="_blank" rel="noopener noreferrer">
                  {{ provider.web }}
                </a>
                <span v-else>--</span></span>
            </div>
            <div class="provider-mobile-card__item">
              <span><b class="provider-mobile-card__label">Telefono:</b>
              {{ provider.phone || '--' }}</span>
            </div>
            <div class="provider-mobile-card__item">
              <span><b class="provider-mobile-card__label">Descripcion:</b>
              {{ provider.serviceDescription || '--' }}</span>
            </div>
          </el-card>
        </el-row>
      </div>
      <div v-else class="mt-3">
        <h5>No se han encontrado proveedores</h5>
      </div>
      <SharedElementPagination class="pagination"
        v-model:current-page="page" :total="totalResults" v-model:page-size="itemsPerPage" style="padding: 0px 0px 20px 0px;"
        @change="page = $event; search()" show-always />
    </div>

    <el-dialog v-model="createOrUpdateProvider" class="dense-dialog el-dialog provider-dialog"
      @closed="closeCreateOrUpdateDialog()" :show-close="false" width="850px" :center="true" :lock-scroll="true"
      :style="{ borderRadius: `var(--el-border-radius-round)` }">
      <div class="my-header">
        <svg id="asterisk" xmlns="http://www.w3.org/2000/svg" width="724" height="1024" viewBox="0 0 724 1024">
          <path
            d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z" />
        </svg>
        <span class="text-header ml-5 mt-3">{{ selectedProvider ? 'Actualizar' : 'Nuevo' }} proveedor</span>
        <img class="close-icon pointer" src="@/assets/images/aspa_blue_small_entrada.png" alt=""
          @click="closeCreateOrUpdateDialog()">
      </div>
      <div class="my-body">
        <form @submit.prevent="">
          <div class="form-group row align-content-center justify-content-center provider-form-row">
            <div class="col-md-10 col-12">
              <div class="form-item">
                <label for="providerName" class="w-100">Nombre del proveedor:</label>
                <el-input v-model="providerName" placeholder="Nombre del proveedor" class="mt-1 w-100" />
                <p v-if="errorProvider.name" class="invalid-feedback mb-0">
                  <small>Este campo es obligatorio</small>
                </p>
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="form-item">
                <label for="providerType" class="w-100">Tipo del proveedor:</label>
                <el-select v-model="providerType" filterable clearable class="w-100" placeholder="Tipo del proveedor">
                  <el-option v-for="item in tempProviderType" :key="item.id" :label="item.name" :value="item.value" />
                </el-select>
                <p v-if="errorProvider.type" class="invalid-feedback mb-0">
                  <small>Este campo es obligatorio</small>
                </p>
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="form-item">
                <span class="provider-form-section-title">Persona responsable</span>
                <div class="row provider-contact-row">
                  <div class="col-md-6 col-12">
                    <label for="providerPointPerson" class="w-100">Nombre:</label>
                    <el-input v-model="providerPointPerson" placeholder="Nombre" class="mt-1 w-100" />
                  </div>
                  <div class="col-md-6 col-12">
                    <label for="providerPhone" class="w-100">Telefono:</label>
                    <el-input v-model="providerPhone" placeholder="Telefono" class="mt-1 w-100" />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="form-item">
                <label for="providerEmail" class="w-100">Correo electronico:</label>
                <el-input v-model="providerEmail" placeholder="Correo electronico" class="mt-1 w-100" />
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="form-item">
                <label for="providerCollaborators" class="w-100">Colaboradores:</label>
                <el-select v-model="providerCollaborators" class="mt-1 w-100 provider-collaborators" multiple filterable
                  clearable placeholder="Inserte el nombre de un colaborador" collapse-tags collapse-tags-tooltip
                  :max-collapse-tags="2" :filter-method="filterCollaboratorsByName">
                  <el-option v-for="item in filteredCollaborators" :key="item.id || item['@id']"
                    :label="item.name + ' ' + item.lastName" :value="item['@id']" />
                </el-select>
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="form-item">
                <label for="providerWeb" class="w-100">Página web:</label>
                <el-input v-model="providerWeb" placeholder="Página web" class="mt-1 w-100" @blur="ensureHttp" />
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="form-item providerDescription">
                <label for="providerDescription" class="w-100">Descripcion:</label>
                <el-input v-model="providerDescription" placeholder="Descripcion" type="textarea"
                  style="font-size: 16px;" rows="3" class="mt-1 w-100" />
              </div>
            </div>
            <div class="col-md-10 col-12">
              <div class="provider-form-actions">
                <button class="btn btn_darkpink provider-submit-button" @click="handleUpdateProvider()">
                  {{ selectedProvider ? 'Actualizar' : 'Nuevo' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </el-dialog>

    <SharedDeleteConfirmDialog v-model="deleteDialogShown" title="Eliminar proveedor"
      message="¿Desea eliminar permanentemente este proveedor?" @confirm="deleteProvider(selectedProvider)"
      @cancel="selectedProvider = null" />
  </div>
</template>

<style lang="scss" scoped>
.provider-input {
  vertical-align: baseline;
  margin: 30px 10px 10px 10px;
}

.provider-search {
  padding: 24px 32px;
}

.provider-search-actions {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 12px;
}

.provider-search-button {
  min-width: 180px;
  max-width: 250px;
}

.provider-search-button--primary {
  color: white;
}

.provider-actions-col {
  width: 110px;
  min-width: 110px;
  text-align: center;
}

.pagination {
  padding: 0px 0px 20px 0px;
}

.table-mobile {
  display: none;
}

.provider-mobile-card {
  border-radius: 18px;
}

.provider-mobile-card :deep(.el-card__body) {
  position: relative;
  padding: 18px 18px 20px;
}

.provider-mobile-card__delete {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
}

.provider-mobile-card__header {
  margin-bottom: 14px;
}

.provider-mobile-card__title {
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  padding-right: 42px;
  word-break: break-word;
}

.provider-mobile-card__item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
  word-break: break-word;
}

.provider-mobile-card__label {
  color: $bg-blue;
  font-weight: 800;
}

.provider-mobile-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 6px;
}

.provider-form-row {
  row-gap: 18px;
}

.provider-form-section-title {
  display: inline-block;
  color: $bg-blue;
  font-size: 1.1rem;
  font-weight: 800;
  margin-bottom: 10px;
}

.provider-contact-row {
  row-gap: 14px;
}

.provider-form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.provider-submit-button {
  min-width: 180px;
}

:deep(.provider-dialog .el-dialog__body) {
  background: #f3f0fb !important;
  min-height: 800px !important;
}

:deep(.provider-dialog .el-dialog__header) {
  padding: 0;
}

.my-header {
  margin-top: 2px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.text-header {
  text-align: left;
  font-size: 4em;
  font-weight: 900;
  letter-spacing: -0.05em;
  color: #f9156f;
}

.close-icon {
  width: 8%;
  height: 20%;
}

#asterisk {
  position: absolute;
  vertical-align: super;
  height: auto;
  width: 2.2em;
  fill: #f9156f;
}

.my-body {
  margin-right: 5%;
}

.providerDescription {
  .el-textarea {
    .el-textarea__inner {
      font-size: 16px !important;
      color: white;
    }
  }
}

.provider-collaborators :deep(.el-select__tags-text) {
  max-width: 160px;
}

.provider-collaborators :deep(.el-select__wrapper) {
  min-height: 44px;
  padding-top: 8px;
  padding-bottom: 8px;
}

.provider-collaborators :deep(.el-select__selection) {
  min-width: 0;
  align-items: center;
}

.provider-collaborators :deep(.el-select__selected-item) {
  min-width: 0;
  max-width: 100%;
}

.provider-collaborators :deep(.el-tag) {
  max-width: calc(100% - 24px);
}

.provider-collaborators :deep(.el-tag__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .table-mobile {
    display: none;
  }
}

@media (max-width: 767px) {
  .title-text {
    font-size: 2.2em;
  }

  .card-search {
    height: auto;
    padding: 20px 16px;
    background-size: 92px;
    background-position: calc(100% + 16px) 12px;
  }

  .provider-search {
    padding: 0;
  }

  .provider-search-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    margin-top: 10px;
  }

  .provider-search-button {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    margin-left: 0 !important;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: block;
    padding-right: 14px;
  }

  .provider-form-actions {
    justify-content: center;
  }

  .provider-submit-button {
    width: 100%;
  }

  .my-header {
    align-items: flex-start;
    gap: 12px;
    padding-right: 10px;
  }

  .text-header {
    font-size: 2em;
    line-height: 1.1;
    margin-top: 0.75rem !important;
  }

  .close-icon {
    width: 26px;
    min-width: 26px;
    margin-top: 12px;
  }

  .my-body {
    margin-right: 0;
  }

  .provider-form-row {
    margin: 0;
  }

  .provider-form-row>div {
    padding-left: 12px;
    padding-right: 12px;
  }

  .provider-contact-row>div {
    padding-left: 12px;
    padding-right: 12px;
  }

  :deep(.provider-dialog) {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
    margin-top: 4vh !important;
  }

  :deep(.provider-dialog .el-dialog__body) {
    min-height: auto !important;
    padding-bottom: 22px;
  }

  .provider-collaborators :deep(.el-select__wrapper) {
    padding-right: 34px;
  }

  .provider-collaborators :deep(.el-select__tags-text) {
    max-width: 120px;
  }
}

@media (max-width: 500px) {
  .provider-collaborators :deep(.el-tag) {
    max-width: 100%;
  }

  .provider-collaborators :deep(.el-select__tags-text) {
    max-width: 96px;
  }

  .provider-collaborators :deep(.el-select__wrapper) {
    min-height: 46px;
    padding-left: 10px;
    padding-right: 34px;
  }
}
</style>
